import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Badge, Button, Card, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconFileText, IconSignature } from '@tabler/icons-react';
import { sellService } from '@/api/services/SellService';
import { orderService } from '@/api/services/OrderService';
import { getApiMessage, isServiceUnavailable } from '@/api/errors';
import type { SaleOfferDraft } from '@/types/sell';

type Status = 'initial' | 'generated' | 'signed' | 'changed';

interface AgentAssignmentBlockProps {
  /** Полный черновик для генерации; null — обязательные поля ещё не заполнены (генерация недоступна). */
  readonly draft: SaleOfferDraft | null;
  /** Подпись значимых полей: при её изменении после генерации требуем сформировать заново. */
  readonly signatureKey: string;
  /** Текущий id поручения (источник истины — у родителя; для edit-префилла). */
  readonly fileId: number | null;
  /** Подписано ли (родитель блокирует сабмит, пока false). */
  readonly signed: boolean;
  readonly onChange: (fileId: number | null, signed: boolean) => void;
}

// Поручение агенту: state-машина initial→generated→signed (+changed при правке значимых полей
// после генерации). Подпись синхронная (besService.signFile). Сабмит предложения возможен только
// когда signed=true. Зеркалит agent-assignment-block старого портала, но без ЭДО-асинхронности.
export default function AgentAssignmentBlock({ draft, signatureKey, fileId, signed, onChange }: AgentAssignmentBlockProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>(signed && fileId != null ? 'signed' : 'initial');
  const keyAtGenerate = useRef<string>(signatureKey);

  // Правка значимых полей после генерации/подписи аннулирует поручение (как старый портал).
  useEffect(() => {
    if ((status === 'generated' || status === 'signed') && signatureKey !== keyAtGenerate.current) {
      setStatus('changed');
      onChange(null, false);
    }
  }, [signatureKey, status, onChange]);

  const onError = (e: unknown): void => {
    notifications.show({
      color: 'red',
      message: isServiceUnavailable(e) ? t('auth.errors.unavailable') : (getApiMessage(e) ?? t('auth.errors.generic')),
    });
  };

  const generate = useMutation({
    mutationFn: () => sellService.generateAgentAssignment(draft!),
    onSuccess: (file) => {
      keyAtGenerate.current = signatureKey;
      setStatus('generated');
      onChange(file.id, false);
      notifications.show({ color: 'lime', message: t('app.sell.form.agent.generated') });
    },
    onError,
  });

  const sign = useMutation({
    mutationFn: () => sellService.signAgentAssignment(fileId!),
    onSuccess: () => {
      setStatus('signed');
      onChange(fileId, true);
      notifications.show({ color: 'lime', message: t('app.sell.form.agent.signed') });
    },
    onError,
  });

  const download = useMutation({
    mutationFn: () => orderService.downloadOrderFile(fileId!),
    onError,
  });

  const canGenerate = draft != null && !generate.isPending;
  const showFile = (status === 'generated' || status === 'signed') && fileId != null;
  const busy = generate.isPending || sign.isPending;

  // Без внешнего Card — секцию оборачивает страница формы (как остальные секции, единообразно).
  return (
      <Stack gap="md">
        <Text size="sm" c="gray.8">
          {t('app.sell.form.agent.intro')}
        </Text>

        {showFile ? (
          <Card withBorder padding="sm" bg="gray.0">
            <Group justify="space-between" wrap="nowrap" gap="sm">
              <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                <ThemeIcon variant="light" color={status === 'signed' ? 'lime' : 'wheat'} radius="md">
                  <IconFileText size={18} />
                </ThemeIcon>
                <Badge variant="light" color={status === 'signed' ? 'lime' : 'wheat'} tt="none" fw={500} radius="sm">
                  {status === 'signed' ? t('app.sell.form.agent.signedBadge') : t('app.sell.form.agent.readyBadge')}
                </Badge>
              </Group>
              <ActionIcon
                variant="default"
                size={34}
                aria-label={t('app.sell.form.agent.download')}
                loading={download.isPending}
                onClick={() => download.mutate()}
              >
                <IconDownload size={18} />
              </ActionIcon>
            </Group>
          </Card>
        ) : status === 'changed' ? (
          <Badge variant="light" color="red" tt="none" fw={500} radius="sm">
            {t('app.sell.form.agent.changedBadge')}
          </Badge>
        ) : null}

        <Group gap="sm" wrap="wrap">
          <Button
            variant="default"
            leftSection={<IconFileText size={16} />}
            loading={generate.isPending}
            disabled={!canGenerate}
            onClick={() => generate.mutate()}
          >
            {status === 'changed' || showFile ? t('app.sell.form.agent.regenerate') : t('app.sell.form.agent.generate')}
          </Button>
          {status === 'generated' ? (
            <Button
              leftSection={<IconSignature size={16} />}
              loading={sign.isPending}
              onClick={() => sign.mutate()}
            >
              {t('app.sell.form.agent.sign')}
            </Button>
          ) : null}
        </Group>

        {draft == null && !busy ? (
          <Text size="xs" c="gray.6">
            {t('app.sell.form.agent.needFillFirst')}
          </Text>
        ) : null}
      </Stack>
  );
}
